import { useEffect, useRef, useState, type CSSProperties, type PointerEvent } from 'react';
import { track } from '../../services/analytics';
import type { Copy } from './copy';

/*
 * The hero's four steps: cook, freeze, thaw, a different lunch. One step is "on" at a
 * time and the headline verbs light up with it. On a wide screen the four sit in a row
 * and a coral line runs from number to number; on a phone they stack into one card
 * that plays like stories, with progress bars on top and a swipe to either side.
 * Autoplay runs only while the hero is on screen and pauses under the mouse. Reduced
 * motion keeps the bars, taps and swipes and drops the autoplay and the slide.
 */

const AUTO_MS = 3200;
/** How far a finger has to travel sideways before it counts as a swipe. */
const SWIPE_PX = 40;
/** Below this width the steps play as stories (landing.css, `.vbars`). */
const STORIES = '(max-width: 699px)';
/** A stories tap in the left third goes back, anywhere else goes forward. */
const BACK_ZONE = 1 / 3;

const IMAGES = ['step-cook', 'step-freeze', 'step-thaw', 'step-lunch'];

export const HeroSteps = ({
  c,
  step,
  onStep,
  lunchProtein,
}: {
  c: Copy;
  step: number;
  onStep: (n: number) => void;
  /** Grams of protein in the step-4 lunch, from the page's example week. */
  lunchProtein: number;
}) => {
  const s = c.hero.steps;
  const total = s.length;
  const ref = useRef<HTMLDivElement>(null);
  const swipeFrom = useRef<number | null>(null);
  const [still] = useState(() => window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  const [visible, setVisible] = useState(false);
  const [hovered, setHovered] = useState(false);
  /** Bumped to restart the running bar and the timer without changing the step. */
  const [run, setRun] = useState(0);
  const playing = !still && visible && !hovered;

  useEffect(() => {
    const el = ref.current;
    if (!el || still) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        setVisible(entry.isIntersecting);
        if (entry.isIntersecting) setRun((r) => r + 1);
      },
      { threshold: 0.3 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [still]);

  useEffect(() => {
    if (!playing) return;
    const timer = window.setTimeout(() => onStep((step + 1) % total), AUTO_MS);
    return () => window.clearTimeout(timer);
  }, [step, run, playing, total, onStep]);

  const go = (to: number, how: 'click' | 'tap' | 'swipe') => {
    const next = (to + total) % total;
    track('landing_hero_stepped', { step: next + 1, how });
    onStep(next);
    setRun((r) => r + 1);
  };

  const onPointerDown = (e: PointerEvent) => {
    swipeFrom.current = e.clientX;
  };
  /* Stories: a swipe or a tap on the card steps forward or back, like Instagram. */
  const onPointerUp = (e: PointerEvent<HTMLDivElement>) => {
    if (swipeFrom.current === null) return;
    const dx = e.clientX - swipeFrom.current;
    swipeFrom.current = null;
    if (Math.abs(dx) > SWIPE_PX) {
      go(step + (dx < 0 ? 1 : -1), 'swipe');
    } else if (window.matchMedia(STORIES).matches) {
      const box = e.currentTarget.getBoundingClientRect();
      const back = e.clientX - box.left < box.width * BACK_ZONE;
      go(step + (back ? -1 : 1), 'tap');
    }
  };
  /* Row: a click picks that step; on the step that's already on it moves to the next. */
  const onTile = (n: number) => {
    if (window.matchMedia(STORIES).matches) return;
    go(n === step ? step + 1 : n, 'click');
  };

  return (
    <div
      className={`vsteps${playing ? '' : ' held'}`}
      ref={ref}
      role="group"
      aria-label={c.hero.stepsLabel}
      style={{ '--i': step, '--auto': `${AUTO_MS}ms` } as CSSProperties}
      onPointerEnter={(e) => e.pointerType === 'mouse' && setHovered(true)}
      onPointerLeave={() => {
        if (!hovered) return;
        setHovered(false);
        setRun((r) => r + 1);
      }}
    >
      <div className="vbars">
        {s.map((st, n) => (
          <button
            type="button"
            key={n}
            className={n < step ? 'done' : n === step ? 'on' : undefined}
            aria-label={st.h}
            aria-current={n === step ? 'step' : undefined}
            onClick={() => go(n, 'click')}
          >
            <span>
              <i key={n === step ? `${step}-${run}` : n} />
            </span>
          </button>
        ))}
      </div>
      <div className="vrow" onPointerDown={onPointerDown} onPointerUp={onPointerUp}>
        <span className="vfill" aria-hidden="true" />
        <ol className="vlist">
          {s.map((st, n) => (
            <li key={n} className={`vstep${n === step ? ' on' : n < step ? ' past' : ''}`}>
              <span className="vnum" aria-hidden="true">
                {n + 1}
              </span>
              <button
                type="button"
                className="vtile"
                aria-label={st.h}
                aria-current={n === step ? 'step' : undefined}
                onClick={() => onTile(n)}
              >
                <span className={`mchip${st.cold ? ' frost' : ''}`}>
                  {st.protein ? (
                    <>
                      <i className="dot" />
                      {st.when} · {lunchProtein} g {c.protein}
                      <sup>*</sup>
                    </>
                  ) : (
                    st.when
                  )}
                </span>
                <img
                  src={`/landing/${IMAGES[n]}.webp`}
                  width={1000}
                  height={1000}
                  alt={st.alt}
                  draggable={false}
                />
              </button>
              <div className="vtxt">
                <h3>{st.h}</h3>
                <p>{st.p}</p>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
};
